import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import AccountContext from '../../services/AccountContext';
import memberService from '../../services/memberApi';
import Error, { getPageNotFoundTitle } from '../../components/Error';
import content from '../../services/content';
import Head from '../../components/head';
import Breadcrumbs from '../../components/Breadcrumbs';
import AdminLinks from '../../components/AdminLinks/AdminLinks';
import CategoryTree from '../../components/CategoryTree/CategoryTree';
import dynamic from 'next/dynamic';
import { Dicon, Button } from 'edcom-ui';
import styles from './lessonPlan.module.less';
import { CollectionsProvider } from '../../services/CollectionsContext';
import { openSignup } from '../../services/regModal';
import { openFeatureWall, openPayWall } from '../../services/payWallModal';
import RelatedGuidedLesson from '../../components/RelatedGuidedLessons';
import track, { trackGA } from '../../services/track';
import LessonPlanSection from './lessonPlanSection';
import Credits from '../../components/Credits';
import { getProductLinkingData } from '../../components/LinkingData/util';

const RelatedLearningResources = dynamic(
  () => import('../../components/RelatedLearningResources'),
  { ssr: false }
);

const ShareOptions = dynamic(() => import('../../components/ShareOptions'), {
  ssr: false,
});

const LessonPlan = ({
  id,
  title,
  urlName,
  internalName,
  isPreLesson = false,
  isSubPacket = false,
  hasElContent = false,
  metaTitle = '',
  metaDescription = '',
  description,
  published = false,
  indexed = false,
  meta,
  linkedLesson = [],
  guidedLesson = [],
  standards = [],
  grades = [],
  subjects = [],
  mainImage = null,
  type = 'lesson-plan',
  error = false,
}) => {
  const [showELContent, setShowELContent] = useState(true);
  const [canDownload, setCanDownload] = useState(false);

  const { memberId, isPremium, isAdmin } = useContext(AccountContext);

  const hideUnpublished = !published && !isAdmin;

  if (
    hideUnpublished ||
    (error && error.statusCode && error.statusCode === 404)
  ) {
    if (!error) error = { statusCode: 404 }; // If the content is unpublished, return a 404 (Not Found) for non-admins
    metaTitle = getPageNotFoundTitle();
  }

  const contentTypeDisplay = 'Lesson Plan';

  const relatedGuidedLesson =
    guidedLesson && guidedLesson.contentCounts !== undefined ? (
      <RelatedGuidedLesson {...guidedLesson} />
    ) : null;

  const mainDownloadURL =
    isSubPacket && meta.body.attachments.length > 0
      ? '/download/lesson-plan/' +
        urlName +
        '/attachments/' +
        meta.body.attachments[0].filename
      : '/download/lesson-plan/' + urlName + '/' + urlName + '.pdf';

  useEffect(() => {
    memberService.checkCanDownload('lesson-plan', id).then(resp => {
      setCanDownload(resp?.allowed === true);
    });
  }, [memberId, isPremium]);

  const trackDownload = () => {
    track('Downloaded Content', {
      contentId: id,
      contentType: 'lesson-plan',
      name: title,
      section: 'Lesson Plan Detail',
    });
    trackGA('lesson-plan', 'download');
  };

  const handleELToggle = () => {
    showELContent ? setShowELContent(false) : setShowELContent(true);
  };

  // Handle clicking download button based on user roles to open paywall or signup modal
  const handleMainAction = async (event, link) => {
    const downloadURL = link ? link : mainDownloadURL;
    // For anonymous clicking download
    if (!memberId) {
      event.preventDefault();
      openSignup('lesson-plan', {
        signUp: { title: 'Join Education.com for Free Access to Lesson Plans' },
      });
      return;
    }

    // For basic member clicking download
    if (!isPremium) {
      event.preventDefault();

      if (isSubPacket) {
        openFeatureWall({
          title: 'Premium only Content',
          text: 'You can only view this lesson plan as a Premium member.',
          feature: 'Lesson Plan',
          contentType: 'lesson-plan',
          contentId: internalName,
        });
        return;
      }

      if (canDownload === true) {
        window.open(downloadURL, '_blank', 'noopener');
        trackDownload();
      } else {
        openPayWall({
          contentId: internalName,
          contentType: 'lesson-plan',
          section: 'Lesson Plan Detail',
          feature: 'Lesson Plan Download',
          name: title,
        });
      }
    } else {
      window.open(downloadURL, '_blank', 'noopener');
      trackDownload();
    }
  };

  const lessonPlanData = {
    urlName: urlName,
    meta: meta,
    showELContent: showELContent,
    handleMainAction: handleMainAction,
  };

  // Build linked lesson HTML
  const renderLinkedLessons = theClassName => {
    if (linkedLesson.length > 0) {
      let className = isPreLesson ? 'is_prelesson' : 'is_lesson';
      let lessonList = [];
      for (let i = 0; i < linkedLesson.length; i++) {
        let separator = '';
        if (i < linkedLesson.length - 2) {
          separator = <span>, </span>;
        } else if (i < linkedLesson.length - 1 && linkedLesson.length === 2) {
          separator = <span> and </span>;
        } else if (i < linkedLesson.length - 1 && linkedLesson.length > 2) {
          separator = <span>, and </span>;
        }

        lessonList.push(
          <>
            <a
              href={'/lesson-plan/' + linkedLesson[i].urlName + '/'}
              key={linkedLesson[i].urlName}
            >
              {linkedLesson[i].title}
            </a>
            {separator}
          </>
        );
      }
      let listHtml = isPreLesson ? (
        <>
          <span>This lesson can be used as a pre-lesson for the </span>
          {lessonList}
          <span> lesson plan{linkedLesson.length === 1 ? '' : 's'}.</span>
        </>
      ) : (
        <>
          <span>Need extra help for EL students? Try the </span>
          {lessonList}
          <span> pre-lesson{linkedLesson.length === 1 ? '' : 's'}.</span>
        </>
      );
      return (
        <div
          className={clsx(styles.linkedLesson, styles[theClassName], className)}
        >
          <div className={styles.elLabel}>
            <i className={styles.iconEL} />
          </div>
          {listHtml}
        </div>
      );
    }
  };

  // Handle a special body-section: academicLanguageHTML
  const renderAcademicLanguage = () => {
    let academicLanguageHTML = [];
    if (meta.body.buildingAcademicLanguage) {
      for (const key of Object.keys(meta.body.buildingAcademicLanguage)) {
        let data = meta.body.buildingAcademicLanguage[key];
        const cloneLessonPlanData = Object.assign({}, lessonPlanData);
        cloneLessonPlanData.meta = { body: {} };
        cloneLessonPlanData.meta.body[key] = data;
        let html = (
          <LessonPlanSection
            lessonPlanData={cloneLessonPlanData}
            title={key}
            sectionName={key}
          />
        );
        if (data && data.order) {
          academicLanguageHTML[
            meta.body.buildingAcademicLanguage[key].order
          ] = html;
        } else if (data && !data.order) {
          academicLanguageHTML.push(html);
        }
      }
    }

    return academicLanguageHTML;
  };

  const thumbnail =
    mainImage === null ? '/files/static/content/LessonPlan.jpg' : mainImage;

  return (
    <>
      <Head
        description={metaDescription}
        index={indexed}
        image={mainImage}
        title={metaTitle}
        linkingData={getProductLinkingData(title, thumbnail, description)}
      >
        <title>{metaTitle}</title>
        <meta property="og:type" content="educationdotcom:lesson-plan" />
      </Head>

      <div className={clsx('page-width', 'typography-small')}>
        {error ? (
          <Error err={error} />
        ) : (
          <>
            <Breadcrumbs
              title={title}
              grades={grades}
              path="lesson-plans"
              subjects={subjects}
              contentType="lesson-plan"
            />
            <div>
              <h3>
                <Dicon name={type} theme="dark" />{' '}
                <span>{contentTypeDisplay}</span>
              </h3>
              <h1>{title}</h1>
              {!!memberId && (
                <ShareOptions
                  name={title}
                  imageSrc={mainImage}
                  contentType={'lesson plan'}
                  contentId={internalName}
                  downloadHref={mainDownloadURL}
                  canDownload={canDownload}
                />
              )}
            </div>
            <div className={styles.contentDetail}>
              <div>
                <div
                  className={styles.lessonPlanSideBar}
                  style={!isPremium ? { marginTop: '0px' } : {}}
                >
                  <div className={styles.lessonPlanDesc}>{description}</div>
                  <Credits refreshCredits={true} gap={16} linkWidth={160} />
                  <Button
                    size="normal"
                    onClick={handleMainAction}
                    target="_blank"
                    className={styles.mainActionButton}
                  >
                    Download lesson plan
                  </Button>

                  <div>{renderLinkedLessons('linkedLessonMobile')}</div>

                  <CategoryTree
                    resource={{
                      grades: grades,
                      subjects: subjects,
                      standards: standards,
                    }}
                  />

                  <aside className="side-box ">
                    <LessonPlanSection
                      lessonPlanData={lessonPlanData}
                      sectionName="materials"
                      title="Materials and preparation"
                      isELField={false}
                    />

                    <LessonPlanSection
                      lessonPlanData={lessonPlanData}
                      sectionName="teacherModelMaterials"
                      title="Teacher model materials"
                      isELField={false}
                    />
                    <LessonPlanSection
                      lessonPlanData={lessonPlanData}
                      sectionName="proficiency"
                      title="EL Proficiency Level"
                      isELField={false}
                    />
                    <LessonPlanSection
                      lessonPlanData={lessonPlanData}
                      sectionName="vocabulary"
                      title="Vocabulary"
                      isELField={false}
                    />
                    <LessonPlanSection
                      lessonPlanData={lessonPlanData}
                      sectionName="mathLanguageRoutine"
                      title="Math Language Routine"
                      isELField={false}
                    />
                    <LessonPlanSection
                      lessonPlanData={lessonPlanData}
                      sectionName="books"
                      title="Related books and/or media"
                      isELField={false}
                    />
                    <LessonPlanSection
                      lessonPlanData={lessonPlanData}
                      sectionName="elBooks"
                      title="Related books and/or media"
                      isELField={false}
                    />
                    <LessonPlanSection
                      lessonPlanData={lessonPlanData}
                      sectionName="keyTerms"
                      title="Key terms"
                      isELField={false}
                    />
                  </aside>
                </div>

                {renderLinkedLessons('linkedLessonMain')}

                <LessonPlanSection
                  lessonPlanData={lessonPlanData}
                  sectionName="synopsis"
                  title=""
                  isELField={false}
                  onlyForPro={false}
                  onlyForLoggedIn={false}
                />
                <LessonPlanSection
                  lessonPlanData={lessonPlanData}
                  sectionName="objectives"
                  title="Learning Objectives"
                  isELField={false}
                  onlyForPro={false}
                  onlyForLoggedIn={false}
                />
                <LessonPlanSection
                  lessonPlanData={lessonPlanData}
                  sectionName="elObjectives"
                  title="Objectives"
                  isELField={false}
                  onlyForPro={false}
                  onlyForLoggedIn={false}
                />
                <LessonPlanSection
                  lessonPlanData={lessonPlanData}
                  sectionName="academicObjectives"
                  title="Academic"
                  isELField={false}
                  onlyForPro={false}
                  onlyForLoggedIn={false}
                />
                <LessonPlanSection
                  lessonPlanData={lessonPlanData}
                  sectionName="languageObjectives"
                  title="Language"
                  isELField={false}
                  onlyForPro={false}
                  onlyForLoggedIn={false}
                />

                <section id="lesson">
                  {hasElContent ? (
                    <div className={clsx(styles.elToggle, 'form')}>
                      <div className={styles.elToggleTip}>
                        <i className="icon-info-circled" />
                        <div className={styles.tip}>
                          The adjustment to the whole group lesson is a
                          modification to differentiate for children who are
                          English learners.
                        </div>
                      </div>
                      <span className={styles.elToggleLabel}>
                        EL adjustments
                      </span>
                      <label
                        id={'el-toggle-switch'}
                        className={clsx(
                          styles.toggleSwitch,
                          showELContent ? styles.elToggleChecked : null
                        )}
                      >
                        <input
                          className="el_switch"
                          type="checkbox"
                          onClick={handleELToggle}
                        />
                        <span className={styles.textChecked}>On</span>
                        <span className={styles.textUnChecked}>Off</span>
                        <div className={styles.switch} />
                      </label>
                    </div>
                  ) : null}

                  <div className="nobreak">
                    <LessonPlanSection
                      lessonPlanData={lessonPlanData}
                      sectionName="introduction"
                      title="Introduction"
                      isELField={false}
                      onlyForLoggedIn={false}
                    />
                    <LessonPlanSection
                      lessonPlanData={lessonPlanData}
                      sectionName="elIntroduction"
                      title="Introduction"
                      onlyForLoggedIn={false}
                    />

                    {!memberId || !isPremium ? (
                      <div className={styles.readMorePayWall}>
                        <Button
                          size="normal"
                          onClick={handleMainAction}
                          target="_blank"
                          className={styles.downloadToReadMore}
                        >
                          Download to read more
                        </Button>
                      </div>
                    ) : null}
                  </div>

                  <LessonPlanSection
                    lessonPlanData={lessonPlanData}
                    sectionName="explicitInstruction"
                    title="Explicit Instruction/Teacher modeling"
                    isELField={false}
                  />
                  <LessonPlanSection
                    lessonPlanData={lessonPlanData}
                    sectionName="elExplicitInstruction"
                    title="Explicit Instruction/Teacher modeling"
                  />

                  <LessonPlanSection
                    lessonPlanData={lessonPlanData}
                    sectionName="guidedPractice"
                    title="Guided Practice"
                    isELField={false}
                  />
                  <LessonPlanSection
                    lessonPlanData={lessonPlanData}
                    sectionName="elGuidedPractice"
                    title="Guided Practice"
                  />

                  <LessonPlanSection
                    lessonPlanData={lessonPlanData}
                    sectionName="independentWorkingTime"
                    title="Independent working time"
                    isELField={false}
                  />
                  <LessonPlanSection
                    lessonPlanData={lessonPlanData}
                    sectionName="elIndependentWorkingTime"
                    title="Independent working time"
                  />

                  <LessonPlanSection
                    lessonPlanData={lessonPlanData}
                    sectionName="studentWorkTime"
                    title="Student work time"
                    isELField={false}
                  />
                  <LessonPlanSection
                    lessonPlanData={lessonPlanData}
                    sectionName="elStudentWorkTime"
                    title="Student work time"
                  />

                  <LessonPlanSection
                    lessonPlanData={lessonPlanData}
                    sectionName="groupWorkTime"
                    title="Group work time"
                    isELField={false}
                  />
                  <LessonPlanSection
                    lessonPlanData={lessonPlanData}
                    sectionName="elGroupWorkTime"
                    title="Group work time"
                  />
                </section>

                <LessonPlanSection
                  lessonPlanData={lessonPlanData}
                  sectionName="differentiation"
                  title="Differentiation"
                  isELField={false}
                />
                <LessonPlanSection
                  lessonPlanData={lessonPlanData}
                  sectionName="technology"
                  title="Technology Integration"
                  isELField={false}
                />
                <LessonPlanSection
                  lessonPlanData={lessonPlanData}
                  sectionName="elTechnology"
                  title="Technology Integration"
                />
                <LessonPlanSection
                  lessonPlanData={lessonPlanData}
                  sectionName="assessment"
                  title="Assessment"
                  isELField={false}
                />
                <LessonPlanSection
                  lessonPlanData={lessonPlanData}
                  sectionName="elAssessment"
                  title="Assessment"
                />

                <LessonPlanSection
                  lessonPlanData={lessonPlanData}
                  sectionName="buildingAcademicLanguage"
                  title="Building Academic Language"
                  isELField={false}
                />

                {renderAcademicLanguage()}

                <LessonPlanSection
                  lessonPlanData={lessonPlanData}
                  sectionName="additionalElAdaptations"
                  title="Additional EL adaptations"
                  isELField={false}
                />
                <LessonPlanSection
                  lessonPlanData={lessonPlanData}
                  sectionName="formativeAssessment"
                  title="Formative Assessment of Academic Language"
                  isELField={false}
                />
                <LessonPlanSection
                  lessonPlanData={lessonPlanData}
                  sectionName="review"
                  title="Review and closing"
                  isELField={false}
                />
                <LessonPlanSection
                  lessonPlanData={lessonPlanData}
                  sectionName="elReview"
                  title="Review and closing"
                />
              </div>
              <div style={{ clear: 'both' }} />
            </div>
          </>
        )}
      </div>

      <CollectionsProvider>
        {relatedGuidedLesson}
        <RelatedLearningResources
          type="lesson-plan"
          id={internalName}
          classString={'page-width typography-small'}
        />
      </CollectionsProvider>

      <AdminLinks>
        <a
          href={`/secure/lesson-plans/editor.php?nocache&type=lesson-plan&plan=${internalName}`}
          target="_blank"
        >
          Edit Lesson Plan
        </a>
        <div>
          <strong>Status: </strong>
          {published ? <span>Published</span> : <span>Unpublished</span>}
        </div>
      </AdminLinks>
    </>
  );
};

LessonPlan.getInitialProps = async ({ asPath, req, query, res }) => {
  const contentService = content(req);
  const { urlname } = query;

  try {
    const response = await contentService.getLessonPlanDetails(urlname);

    // Return a 404 (Not Found) for unpublished resources
    if (!response.published) res.statusCode = 404;

    // Footer props for Tracking
    response.footerProps = {
      grades: response?.grades
        ? response.grades.map(grade => grade.urlName)
        : [],
      subjects: response?.subjects
        ? response.subjects.map(subject => subject.internalName)
        : [],
      contentType: 'lesson-plan',
      pageType: 'Content',
    };

    return {
      ...response,
      pageKey: asPath,
    };
  } catch (error) {
    if (error.statusCode === 404) {
      res.statusCode = 404;
    }
    return { error };
  }
};

const categoryShape = PropTypes.shape({
  urlName: PropTypes.string,
  internalName: PropTypes.string,
  name: PropTypes.string,
});

LessonPlan.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  urlName: PropTypes.string.isRequired,
  internalName: PropTypes.string.isRequired,
  isPreLesson: PropTypes.bool,
  hasElContent: PropTypes.bool,
  isSubPacket: PropTypes.bool,
  metaTitle: PropTypes.string,
  description: PropTypes.string.isRequired,
  metaDescription: PropTypes.string,
  published: PropTypes.bool,
  indexed: PropTypes.bool,
  meta: PropTypes.object,
  linkedLesson: PropTypes.array,
  guidedLesson: PropTypes.array,
  updatedAt: PropTypes.string,
  createdAt: PropTypes.string,
  standards: PropTypes.arrayOf(categoryShape),
  grades: PropTypes.arrayOf(categoryShape),
  subjects: PropTypes.arrayOf(categoryShape),
  type: PropTypes.string,
  mainImage: PropTypes.string,
  error: PropTypes.object,
};

export default LessonPlan;
